/**
 * based on https://zocada.com/compress-resize-images-javascript-browser/
 *  */
import { inject, Injectable } from '@angular/core';
import { catchError, concatMap, map, mergeMap, tap } from 'rxjs/operators';
import { forkJoin, Observable, Observer, throwError } from 'rxjs';
import { ImageUploadData } from '@lib-ui/lib/models/image-upload-data';
import { ImageDimension } from '@lib-ui/lib/models/image-dimension';
import { LoggingService } from '@lib-ui/lib/services/logging.service';
import { FileDetails } from '@zapp/core/models/file-details';

interface CompressionSettings {
  size: number;
  quality: number;
  grayscale: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ImageRecordService {
  private loggingService = inject(LoggingService);
  private compressionSettings: CompressionSettings = {
    size: 2048,
    quality: 0.5,
    grayscale: false,
  };

  /**
   * crops and compress an image
   * @param file image to crop
   * @returns Promise<Blob>
   */
  compress(file: File): Observable<File> {
    const edgeLength = this.compressionSettings.size; // For scaling relative to width
    console.log('original image size :' + Math.round(file.size / 1024) + 'kb');


    return new Observable((observer: Observer<File>) => {
      const fileReader = new FileReader();

      if (edgeLength == 0) {
        console.log(
          'no changes image size :' + Math.round(file.size / 1024) + 'kb',
        );
        observer.next(file);
        observer.complete();
        return;
      }
      fileReader.onload = () => {
        const img = new Image();
        img.src = fileReader.result as string;
        img.onerror = error => {
          console.log('error loading image');
          observer.error(error);
        };
        img.onload = () => {
          const elem = document.createElement('canvas');
          const scaleWidth = edgeLength / img.width;
          const scaleHeight = edgeLength / img.height;
          const scale = Math.min(scaleWidth, scaleHeight);

          const resizedWidth = img.width * scale;
          const resizedHeight = img.height * scale;

          elem.width = resizedWidth;
          elem.height = resizedHeight;
          const ctx = <CanvasRenderingContext2D>elem.getContext('2d');
          ctx.filter = this.compressionSettings.grayscale
            ? 'grayscale(100%)'
            : 'none';
          ctx.drawImage(
            img,
            0,
            0,
            resizedWidth,
            resizedHeight,
          );

          ctx.canvas.toBlob(
            (blob) => {
              console.log(
                'compressed blob size :' + Math.round(blob!.size / 1024) + 'kb',
              );
              observer.next(
                new File([blob!], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                }),
              );
              observer.complete();
            },
            'image/jpeg',
            this.compressionSettings.quality,
          );
        };
      };

      fileReader.onerror = (error) => observer.error(error);
      fileReader.readAsDataURL(file);
    });
  }

  /**
   * converts image to an DataURL string.
   * @param imageFile blob data of an image
   * @returns Promise<string> with DataURL string
   */
  public imageToURLstring(imageFile: File): Observable<string> {
    if (imageFile == null) {
      return throwError(new Error(
        'empty image fileBlob',
      ));
    }
    return new Observable((observer: Observer<string>) => {
      const reader = new FileReader();
      reader.onload = () => {
        observer.next(reader.result as string);
        observer.complete();
      };
      reader.readAsDataURL(imageFile);
    });
  }

  /**
   * generate Universal Unique Identifier
   * @returns UUID string
   */
  public generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      (c) => {
        const r = (Math.random() * 16) | 0,
          v = c == 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      },
    );
  }

  /**
   * get width and height from an image string
   * @param base64String
   * @returns number[width, height]
   */
  public getImageSizeFromString(base64String: string): Observable<ImageDimension> {
    const img = new Image();
    return new Observable((observer: Observer<ImageDimension>) => {
      img.onload = () => {
        if (!img.width || !img.height) {
          observer.error('image has no width or height');
          return;
        }
        observer.next({ width: img.width, height: img.height });
        observer.complete();
      };
      img.src = base64String;
    });
  }

  public isPortraitImage(file: File) {
    return this.imageToURLstring(file).pipe(
      mergeMap(tempImageString => this.getImageSizeFromString(tempImageString)),
      map(dimensions => dimensions.width <= dimensions.height),
    );
  }

  public prepareForSave(file: File, name: string): Observable<ImageUploadData> {

    const compressedImage$ = this.compress(file);
    const fileString$ = compressedImage$.pipe(
      concatMap(compressed => this.imageToURLstring(compressed)),
    );
    const dimensions$ = fileString$.pipe(
      concatMap(fileString => this.getImageSizeFromString(fileString)),
    );


    return forkJoin([compressedImage$, fileString$, dimensions$]).pipe(
      map(([compressedImage, fileString, imageDimension]) => {
        return { name: name, file: compressedImage, fileString: fileString, dimensions: imageDimension };
      }),
    );
  }

  public createFileDetails(file: File): Observable<FileDetails> {
    const fileName = file.name;
    const compressedImage$ = this.compress(file);
    const imageUrl$ = compressedImage$.pipe(
      concatMap((compressedImage) => {
        return this.imageToURLstring(compressedImage);
      }),
    );
    return forkJoin([imageUrl$, compressedImage$]).pipe(
      catchError(err => {
        this.loggingService.logError(err);
        return throwError(err);
      }),
      tap(() => {
        this.loggingService.logTrace('Image captured: ' + fileName);
      }),
      map(([dataUrl, data]) => ({
        fileName,
        dataUrl,
        data,
      })),
    );
  }
}
